/**
 * Login Page
 *
 * Supabase Auth (email + password). Resolves `public.users` by
 * matching `users.id` to `auth.users.id` after sign-in.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { PrioritiesLogo } from '../../components/ui/PrioritiesLogo';
import { PeakPrioritiesLogo } from '../../components/ui/PeakPrioritiesLogo';
import { btnSecondary } from '../../components/mono/primitives';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { isSuperAdminBySupabaseUserId } from '../../lib/superAdminService';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import type { ActorRole } from '../../types/prioritization/roles';

const ROLE_PRIORITY: ActorRole[] = [
  'system-admin',
  'platform-admin',
  'tenant-admin',
  'workspace-admin',
];

function pickBestRole(roleNames: string[]): ActorRole {
  for (const r of ROLE_PRIORITY) {
    if (roleNames.includes(r)) return r;
  }
  return 'workspace-admin';
}

const UNKNOWN_USER_ID = 'unknown';

/** Supabase GoTrue often returns raw implementation errors; map the worst offenders for humans. */
function signInErrorMessage(raw: string): string {
  const t = raw.trim();
  if (/database error querying schema/i.test(t)) {
    return (
      'We could not finish signing you in. Your login account may be incomplete or misconfigured on the server ' +
      '(this is usually not a wrong password). Ask your administrator to fix the account, or try again in a few minutes.'
    );
  }
  return t;
}

/** Where to send the user after a successful session. Avoid `/` (was login redirect) and auth loops. */
const DEFAULT_AFTER_LOGIN = '/prioritization/dashboard';

function postLoginPath(fromPath: string | undefined, fromSearch?: string): string {
  const p = (fromPath ?? '').trim();
  if (!p || p === '/' || p.startsWith('/auth/')) {
    return DEFAULT_AFTER_LOGIN;
  }
  // Keep the query string — a Plan of Record share link carries its key as ?t=.
  const q = (fromSearch ?? '').trim();
  return q.startsWith('?') ? `${p}${q}` : p;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useUserStore((s) => s.userId);
  const setUser = useUserStore((s) => s.setUser);
  const clearImpersonationState = useUserStore((s) => s.clearImpersonationState);
  const fromState = (location.state as { from?: { pathname: string; search?: string } } | undefined)?.from;
  const from = postLoginPath(fromState?.pathname, fromState?.search);
  const switchRole = useUserStore((s) => s.switchRole);
  const fetchTenants = useTenantStore((s) => s.fetchTenants);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Passwordless: a sign-in link by email. The person this exists for cannot
  // be asked to remember a password; the link lands them on their home.
  const [linkState, setLinkState] = useState<'idle' | 'sending' | 'sent'>('idle');

  const sendSignInLink = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Enter your email address first, then ask for a sign-in link.');
      return;
    }
    setError(null);
    setLinkState('sending');
    try {
      const supabase = getSupabaseClient();
      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: { emailRedirectTo: `${window.location.origin}/`, shouldCreateUser: false },
      });
      if (otpErr) {
        setError(signInErrorMessage(otpErr.message || 'Could not send a sign-in link.'));
        setLinkState('idle');
        return;
      }
      setLinkState('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a sign-in link.');
      setLinkState('idle');
    }
  };

  // Redirect when already authenticated (e.g. user opened /auth/login with a valid session).
  // Do not run while `loading` is true — otherwise setUser during sign-in triggers a navigate
  // before fetchTenants() finishes, causing an extra remount and home/dashboard flash.
  useEffect(() => {
    if (loading) return;
    if (userId && userId !== UNKNOWN_USER_ID && location.pathname.startsWith('/auth/login')) {
      navigate(from, { replace: true });
    }
  }, [userId, from, navigate, loading, location.pathname]);

  const applyUserSession = async (userId: string, userName: string, userEmail: string) => {
    const supabase = getSupabaseClient();
    // Platform admins (super_admins) always get platform-admin role, not steward
    const isPlatformAdmin = await isSuperAdminBySupabaseUserId(userId);
    if (isPlatformAdmin) {
      clearImpersonationState();
      setUser(userId, userName, userEmail);
      switchRole('platform-admin');
      await fetchTenants(userId);
      return;
    }
    const { data: assignments } = await supabase
      .from('role_assignments')
      .select('role_id, roles(name)')
      .eq('subject_type', 'user')
      .eq('subject_id', userId)
      .eq('active', true);
    const roleNames: string[] = [];
    if (assignments) {
      for (const a of assignments) {
        const name = (a.roles as { name?: string } | null)?.name;
        if (name) roleNames.push(name);
      }
    }
    const role = pickBestRole(roleNames) as ActorRole;
    clearImpersonationState();
    setUser(userId, userName, userEmail);
    switchRole(role);
    await fetchTenants(userId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Enter your email address.');
      return;
    }
    setLoading(true);
    try {
      const supabase = getSupabaseClient();

      if (!password) {
        setError('Enter your password.');
        setLoading(false);
        return;
      }

      const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({
        email: trimmed,
        password,
      });
      if (authErr) {
        setError(signInErrorMessage(authErr.message || 'Invalid email or password.'));
        setLoading(false);
        return;
      }
      const authUser = authData.user;
      if (!authUser) {
        setError('Login failed.');
        setLoading(false);
        return;
      }
      const { data: userRow } = await supabase
        .from('users')
        .select('id, name, email')
        .eq('id', authUser.id)
        .maybeSingle();
      const userName = (userRow?.name as string) || authUser.user_metadata?.full_name || trimmed.split('@')[0];
      const userEmail = (userRow?.email as string) || authUser.email || trimmed;
      await applyUserSession(authUser.id, userName, userEmail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    'no-dark w-full px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule-bright)] text-[var(--text-pri)] placeholder:text-[var(--text-dim)] focus:border-[var(--text-pri)] focus:ring-1 focus:ring-[var(--text-pri)] dark:focus:ring-[var(--active-tint)] dark:focus:border-[var(--slate)] outline-none transition-all';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] px-4 py-12 transition-colors duration-300">
      <div className="w-full max-w-md">
        {/* The product's own mark above the form; the practice's logo below it. */}
        <div className="mb-8 flex justify-center">
          <PrioritiesLogo height={34} />
        </div>

        {/* Paper panel, hairline, gold rule on top — the design language's callout, as a form.
            The top colour is an inline style: `.dark * { border-color }` in index.css comes
            after Tailwind's plain classes and would flatten one to grey in dark mode. */}
        <div
          className="bg-[var(--bg-raised)] border border-[var(--rule)] border-t-[3px] p-8 sm:p-10"
          style={{ borderTopColor: 'var(--mustard)' }}
        >
          <h1 className="font-serif text-4xl font-light leading-tight tracking-[-0.01em] text-[var(--text-pri)]">
            Sign in
          </h1>
          <div className="mt-5 mb-7 h-px w-9" style={{ backgroundColor: 'var(--mustard)' }} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className={fieldClass}
                disabled={loading}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-[var(--text-sec)]">
                  Password
                </label>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-[var(--mustard)] underline-offset-4 hover:underline"
                  tabIndex={-1}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="flex gap-2">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  autoComplete="current-password"
                  className={`${fieldClass} flex-1`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="px-3 text-[var(--text-ter)] hover:text-[var(--text-pri)] border border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:bg-[var(--hover-tint)]"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 text-sm text-[var(--coral)] bg-[var(--coral-dim)] p-3" style={{ borderLeft: '3px solid var(--coral)' }}>
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[var(--cta)] text-[var(--cta-ink)] font-heading font-semibold hover:bg-[var(--cta-hover)] disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[var(--rule)] text-center">
            {linkState === 'sent' ? (
              <p role="status" className="text-sm text-[var(--text-sec)]">
                Check your email. Open the link in the message and you will be signed in — no password needed.
              </p>
            ) : (
              <button
                type="button"
                onClick={sendSignInLink}
                disabled={linkState === 'sending'}
                {...btnSecondary('w-full normal-case tracking-normal font-body text-sm')}
              >
                {linkState === 'sending' ? 'Sending your link…' : 'Email me a sign-in link instead'}
              </button>
            )}
          </div>

          <p className="mt-6 text-center text-sm leading-relaxed text-[var(--text-ter)] [text-wrap:balance]">
            Don’t have an account? Ask an admin to send you an invite link — you’ll set your password when you accept it.
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center gap-6">
          <ThemeToggle variant="labeled" />
          <PeakPrioritiesLogo height={16} className="text-[var(--text-ter)]" />
        </div>
      </div>
    </div>
  );
}
